import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Printer, Loader2 } from 'lucide-react';

import { API } from '@/lib/apiBase';

export default function AdminPrintFormats() {
  const { getAuthHeaders } = useAuth();
  const { t } = useLanguage();
  const [formats, setFormats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', price: '', is_active: true });
  const [saving, setSaving] = useState(false);

  const fetchFormats = async () => {
    try {
      const res = await fetch(`${API}/admin/print-formats`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) setFormats(await res.json());
    } catch (e) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFormats(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: mount-only fetch

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '', price: '', is_active: true });
    setDialogOpen(true);
  };

  const openEdit = (fmt) => {
    setEditing(fmt);
    setForm({ name: fmt.name, description: fmt.description || '', price: String(fmt.price), is_active: fmt.is_active });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.price) {
      toast.error(t('prints.fillRequired'));
      return;
    }
    setSaving(true);
    try {
      const url = editing
        ? `${API}/admin/print-formats/${editing.format_id}`
        : `${API}/admin/print-formats`;
      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: form.name,
          description: form.description,
          price: parseFloat(form.price),
          is_active: form.is_active
        })
      });
      if (res.ok) {
        toast.success(editing ? t('prints.formatUpdated') : t('prints.formatCreated'));
        setDialogOpen(false);
        fetchFormats();
      } else {
        toast.error(t('common.error'));
      }
    } catch (e) {
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (formatId) => {
    if (!window.confirm(t('prints.confirmDelete'))) return;
    try {
      const res = await fetch(`${API}/admin/print-formats/${formatId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) {
        toast.success(t('prints.formatDeleted'));
        fetchFormats();
      }
    } catch (e) {
      toast.error(t('common.error'));
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-12" data-testid="admin-print-formats">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">{t('prints.manageFormats')}</h1>
          <p className="text-muted-foreground">{t('prints.manageFormatsDesc')}</p>
        </div>
        <Button onClick={openCreate} className="bg-primary hover:bg-primary/90" data-testid="add-format-btn">
          <Plus className="w-4 h-4 mr-2" />
          {t('prints.addFormat')}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : formats.length === 0 ? (
        <div className="text-center py-24">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <Printer className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-bold mb-2">{t('prints.noFormats')}</h3>
          <p className="text-muted-foreground mb-6">{t('prints.noFormatsDesc')}</p>
          <Button onClick={openCreate} className="bg-primary hover:bg-primary/90">
            <Plus className="w-4 h-4 mr-2" />
            {t('prints.addFormat')}
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {formats.map((fmt) => (
            <Card key={fmt.format_id} className="bg-card border-border" data-testid={`format-${fmt.format_id}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-muted rounded-sm flex items-center justify-center">
                      <Printer className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{fmt.name}</h3>
                      {fmt.description && (
                        <p className="text-sm text-muted-foreground">{fmt.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="font-mono text-xl font-bold text-primary">CHF {fmt.price?.toFixed(2)}</div>
                      <span className={`text-xs font-mono uppercase ${fmt.is_active ? 'text-green-500' : 'text-muted-foreground'}`}>
                        {fmt.is_active ? t('prints.active') : t('prints.inactive')}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(fmt)} data-testid={`edit-format-${fmt.format_id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(fmt.format_id)} className="hover:text-destructive" data-testid={`delete-format-${fmt.format_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>{editing ? t('prints.editFormat') : t('prints.createFormat')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>{t('prints.formatName')}</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t('prints.formatNamePlaceholder')}
                className="bg-secondary border-border mt-1"
                data-testid="format-name-input"
              />
            </div>
            <div>
              <Label>{t('prints.formatDescription')}</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={t('prints.descriptionPlaceholder')}
                className="bg-secondary border-border mt-1"
                data-testid="format-description-input"
              />
            </div>
            <div>
              <Label>{t('admin.price')}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="0.00"
                className="bg-secondary border-border mt-1"
                data-testid="format-price-input"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                data-testid="format-active-switch"
              />
              <Label>{t('prints.isActive')}</Label>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-format-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editing ? t('common.update') : t('common.create')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
